/**
 * Telegram-воркспейс: три колонки (§4 ТЗ).
 *
 * Левая  — аккаунты, режим «Все аккаунты», фильтры, список чатов.
 * Центр  — история выбранного чата и поле отправки.
 * Правая — карточка чата, ответственные, внутренние заметки.
 *
 * Компонент держит состояние выбора и подписки realtime; отрисовкой занимаются
 * дочерние. Разделение важно: логика «что показывать» зависит от прав, и её
 * лучше держать в одном месте.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { messageSummary } from "@/lib/message-summary.ts";
import { sameMessage, telegramIdOf } from "@/lib/message-id.ts";
import {
  CHAT_ACTION_TTL_MS,
  chatActionLabel,
  type ChatActivity,
} from "@/lib/chat-actions.ts";
import type {
  AnyTelegramAccountDto,
  TelegramChatDto,
  TelegramMessageDto,
} from "@tw/shared-types";
import { accountRoom, chatRoom } from "@tw/shared-types";
import { RealtimeClient, type ConnectionStatus } from "../lib/ws.js";
import { useSession } from "../state/session.tsx";
import { AccountSidebar, type AccountSelection } from "../components/AccountSidebar.tsx";
import { ChatList } from "../components/ChatList.tsx";
import { MessageList } from "../components/MessageList.tsx";
import { Composer } from "../components/Composer.tsx";
import { ChatDetails } from "../components/ChatDetails.tsx";
import { BulkBar } from "../components/BulkBar.tsx";
import { FolderBar } from "../components/FolderBar.tsx";
import type { TgFolderDto } from "../lib/api.js";
import { ForwardBar } from "../components/ForwardBar.tsx";
import { TelegramSearch } from "../components/TelegramSearch.tsx";
import { notifyIncoming } from "../lib/notifications.js";
import type { FolderDto } from "../lib/api.js";
import { AlertIcon, PlusIcon, SearchIcon } from "../components/icons.tsx";
import { cn } from "@/lib/utils";
import { EmptyCardArt, EmptyChatArt } from "../components/EmptyArt.tsx";
import { useIsMobile } from "../lib/useMedia.ts";
import { OPEN_CHAT_EVENT, takePendingChat } from "../lib/deepLink.ts";
import { mergeLatest, prependOlder } from "../lib/history.ts";

/** null означает режим «Все аккаунты» (§5 ТЗ). */

interface WorkspaceViewProps {
  /**
   * Показывать ли левую колонку.
   *
   * Состояние живёт в оболочке, а не здесь. Причина простая и выяснилась на
   * первой же проверке: переключатель у кромки колонки исчезает вместе с
   * колонкой, и свернуть её получалось, а развернуть было уже нечем. Кнопка
   * обязана жить там, где она видна в обоих положениях, — то есть в шапке.
   */
  sidebarOpen: boolean;
  /** Открыта ли карточка чата справа. Управляется кнопкой в шапке. */
  detailsOpen: boolean;
}

export function WorkspaceView({
  sidebarOpen,
  detailsOpen,
}: WorkspaceViewProps) {
  const { api, can } = useSession();

  const [accounts, setAccounts] = useState<AnyTelegramAccountDto[]>([]);
  const [selectedAccount, setSelectedAccount] =
    useState<AccountSelection>({ kind: "all" });
  const [chats, setChats] = useState<TelegramChatDto[]>([]);
  const [activeChat, setActiveChat] = useState<TelegramChatDto | null>(null);
  const [messages, setMessages] = useState<TelegramMessageDto[]>([]);
  /**
   * Курсор к более ранним сообщениям открытого чата. null — раньше ничего нет
   * (или ещё не загружено).
   *
   * Раньше лента брала последние 50 сообщений и на этом останавливалась:
   * прокрутка вверх ничего не подгружала, а у холодного чата первая страница
   * и вовсе бывала из пары сообщений.
   */
  const [olderCursor, setOlderCursorState] = useState<string | null>(null);
  const olderCursorRef = useRef<string | null>(null);
  const setOlderCursor = useCallback((value: string | null) => {
    olderCursorRef.current = value;
    setOlderCursorState(value);
  }, []);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const loadingOlderRef = useRef(false);
  /**
   * Кто сейчас печатает — по чатам.
   *
   * Живёт только в памяти и только пока приходят действия: каждое продлевает
   * срок, таймер снимает истёкшие. Показывается в списке чатов вместо
   * превью и в шапке открытого чата вместо статуса — как в Telegram.
   */
  const [activity, setActivity] = useState<Record<string, ChatActivity>>({});
  const [search, setSearch] = useState("");
  /*
   * Поиск уходит на сервер с паузой в 250 мс. Без неё каждая буква — отдельный
   * запрос inbox, и ответы приходят вперемешку: короткий префикс, отвеченный
   * позже, затирал выдачу по полному слову.
   */
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 250);
    return () => clearTimeout(timer);
  }, [search]);
  /**
   * Открыт ли поиск по Telegram.
   *
   * Состояние здесь, а не внутри компонента: кнопка, которая его открывает,
   * стоит в строке поиска, а сам блок раскрывается ниже — это два разных
   * места, и общее у них только состояние.
   */
  const [telegramSearchOpen, setTelegramSearchOpen] = useState(false);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [archivedView, setArchivedView] = useState(false);
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(
    new Set(),
  );
  const [bulkBusy, setBulkBusy] = useState(false);
  const [selectedMessageIds, setSelectedMessageIds] = useState<
    ReadonlySet<string>
  >(new Set());
  const [folders, setFolders] = useState<FolderDto[]>([]);
  const [activeFolderId, setActiveFolderId] = useState<string | null>(null);
  /** Папки Telegram выбранного аккаунта и активная из них. */
  const [tgFolders, setTgFolders] = useState<TgFolderDto[]>([]);
  const [activeTgFolder, setActiveTgFolder] = useState<number | null>(null);
  const [wsStatus, setWsStatus] = useState<ConnectionStatus>("connecting");
  const [loadingChats, setLoadingChats] = useState(true);
  /** Курсор следующей страницы inbox; null — список показан целиком. */
  const [chatsCursor, setChatsCursor] = useState<string | null>(null);
  const [loadingMoreChats, setLoadingMoreChats] = useState(false);
  /** Подгрузка страницы упала — повтор только по кнопке, не сам по себе. */
  const [loadMoreFailed, setLoadMoreFailed] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);

  /*
   * На что отвечаем.
   *
   * Композер умел подставлять цитату с самого начала, но задать её было
   * нечем: проп существовал, а передавать его никто не передавал. То есть
   * ответ на сообщение — одно из самых частых действий в мессенджере —
   * работал только в коде.
   */
  const [replyTo, setReplyTo] = useState<{
    telegramMessageId: string;
    preview: string;
  } | null>(null);

  const realtime = useRef<RealtimeClient | null>(null);

  /*
   * Обработчик realtime создаётся один раз и замыкает состояние на момент
   * подписки. Читать оттуда activeChat и chats напрямую нельзя — они навсегда
   * останутся начальными. Пересоздавать подписку на каждое изменение тоже
   * нельзя: это переподключение сокета при каждом клике по чату.
   */
  const activeChatRef = useRef<TelegramChatDto | null>(null);

  /*
   * Телефон: одна колонка. Нет открытого чата — список; открыт — переписка
   * на весь экран с кнопкой «Назад»; карточка чата — поверх.
   */
  const mobile = useIsMobile();
  const [mobileDetails, setMobileDetails] = useState(false);

  /**
   * Счётчики непрочитанного по аккаунтам — в левой колонке.
   *
   * ── Что здесь чинится ─────────────────────────────────────────────────
   *
   * Они приходили вместе со списком аккаунтов один раз при входе и дальше
   * не менялись ни от прочтения, ни от новых сообщений: строка чата
   * обнулялась, а цифра у аккаунта висела до перезагрузки страницы. Выглядело
   * как «счётчик не уменьшается», хотя чат уже был прочитан.
   *
   * Перечитываем только счётчики, не весь список аккаунтов, и с задержкой:
   * пачка апдейтов после открытия чата — один запрос.
   */
  const unreadTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refreshUnread = useCallback(() => {
    if (unreadTimer.current) clearTimeout(unreadTimer.current);
    unreadTimer.current = setTimeout(() => {
      unreadTimer.current = null;
      void api
        .unreadCounts()
        .then(({ counts }) =>
          setAccounts((prev) =>
            prev.map((a) => ({ ...a, unreadCount: counts[a.id] ?? 0 })),
          ),
        )
        .catch(() => undefined);
    }, 400);
  }, [api]);
  const chatsRef = useRef<TelegramChatDto[]>([]);

  /*
   * Номера запросов списка и истории.
   *
   * Ответы приходят не в том порядке, в каком ушли: смена фильтра, поиск,
   * докачка, быстрый переход между чатами. Применяется только ответ на
   * последний запрос — иначе запоздавший ответ показывает список под старый
   * фильтр или историю чата, из которого уже ушли.
   */
  const chatsSeq = useRef(0);
  const historySeq = useRef(0);

  /**
   * Включён ли хоть один фильтр списка.
   *
   * Realtime-обработчику он нужен, чтобы не вставлять в отфильтрованный
   * список чаты, которые под фильтр не подходят: в «Архиве» или в выдаче
   * поиска внезапно появлялся рабочий чат, получивший сообщение.
   */
  const filteredRef = useRef(false);

  /** Перечитать список и открытую историю — после обрыва WebSocket. */
  const resyncRef = useRef<() => void>(() => undefined);

  // Снятие истёкших «печатает». Один общий таймер, а не по одному на чат:
  // действий может быть много, и их срок один и тот же.
  useEffect(() => {
    const ids = Object.keys(activity);
    if (ids.length === 0) return;
    const timer = setInterval(() => {
      const now = Date.now();
      setActivity((prev) => {
        const next = Object.fromEntries(
          Object.entries(prev).filter(([, a]) => a.until > now),
        );
        return Object.keys(next).length === Object.keys(prev).length ? prev : next;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [activity]);

  useEffect(() => {
    activeChatRef.current = activeChat;
  }, [activeChat]);

  useEffect(() => {
    chatsRef.current = chats;
  }, [chats]);

  // ── Realtime ───────────────────────────────────────────────────────────────

  useEffect(() => {
    const client = new RealtimeClient();
    realtime.current = client;

    /*
     * Пока сокет лежал, события терялись: новые сообщения, прочтения, смены
     * статуса. Сервер их не досылает, поэтому после переподключения
     * перечитываем список и открытую историю сами. Первое подключение не в
     * счёт — данные и так грузятся при входе.
     */
    let wasClosed = false;
    const offStatus = client.onStatus((status) => {
      setWsStatus(status);
      if (status === "closed") wasClosed = true;
      else if (status === "open" && wasClosed) {
        wasClosed = false;
        resyncRef.current();
      }
    });

    const offMessage = client.onMessage((message) => {
      switch (message.type) {
        case "chat-updated":
          setChats((prev) =>
            filteredRef.current
              ? replaceChat(prev, message.chat)
              : upsertChat(prev, message.chat),
          );
          setActiveChat((prev) =>
            prev?.id === message.chat.id ? message.chat : prev,
          );
          refreshUnread();
          break;

        case "message-created":
        case "message-updated": {
          /*
           * Проверка чата ОБЯЗАТЕЛЬНА, и её тут не было.
           *
           * Событие приходит двумя путями: в комнату чата и в комнату
           * аккаунта. На комнату аккаунта клиент подписан всегда — она нужна
           * для списка чатов. Без сверки chatId сообщение из любого другого
           * диалога того же аккаунта дописывалось в ОТКРЫТУЮ переписку.
           *
           * Выглядело это не как сбой, а как чужая реплика в разговоре: текст
           * настоящий, время настоящее, просто адресат другой. Менеджер, у
           * которого один аккаунт на несколько диалогов, отвечал бы не тому.
           */
          const openChatId = activeChatRef.current?.id;
          if (openChatId === message.chatId) {
            const incoming = message.message;
            setMessages((prev) => {
              /*
               * Сверка и по номеру Telegram: отправленное через систему лежит
               * в ленте под нашим UUID, а событие о нём (удаление — всегда)
               * приходит как tg:N. По одному id копия не находилась, и
               * «изменение» дописывалось в конец новым пузырём-призраком.
               */
              const index = prev.findIndex((m) => sameMessage(m, incoming));
              if (index === -1) {
                // Неизвестное сообщение добавляет только «создано»: правка
                // или удаление того, чего нет в загруженной истории, — не
                // повод рисовать его внизу ленты.
                return message.type === "message-created" ? [...prev, incoming] : prev;
              }
              const next = prev.slice();
              // Удаление приходит заглушкой без времени и направления —
              // оставляем пузырь на своём месте, меняем только состояние.
              next[index] =
                incoming.deliveryState === "DELETED"
                  ? { ...prev[index]!, deliveryState: "DELETED" }
                  : incoming;
              return next;
            });
            // Входящее в открытый чат прочитано в момент показа — как в
            // Telegram. Иначе счётчик рос на чате, в который смотрят.
            const telegramId = telegramIdOf(incoming);
            if (message.type === "message-created" && incoming.direction === "INBOUND" && telegramId) {
              markRead(message.chatId, [telegramId]);
            }
          }

          if (message.type === "message-created") {
            /*
             * Чата может не быть в загруженном списке.
             *
             * Inbox отдаёт полсотни чатов, а сообщение приходит по комнате
             * аккаунта — то есть из любого его диалога, включая те, что не
             * попали в текущую выборку. Раньше это давало оповещение без
             * названия: «Новое сообщение» и текст, а от кого — неизвестно.
             *
             * Незнакомый чат дочитываем и кладём в список. Это чинит сразу
             * две вещи: подпись оповещения и саму ленту — активный диалог
             * обязан в ней появиться, а не ждать перезагрузки страницы.
             */
            const known = chatsRef.current.find((c) => c.id === message.chatId);

            if (known) {
              notifyIncoming({
                chatId: message.chatId,
                message: message.message,
                openChatId: openChatId ?? null,
                chats: chatsRef.current,
              });
            } else {
              void api
                .chat(message.chatId)
                .then((chat) => {
                  setChats((prev) =>
                    filteredRef.current ? replaceChat(prev, chat) : upsertChat(prev, chat),
                  );
                  notifyIncoming({
                    chatId: message.chatId,
                    message: message.message,
                    openChatId: openChatId ?? null,
                    chats: [chat],
                  });
                })
                .catch(() => {
                  // Чат мог стать недоступным между событием и запросом.
                  // Оповестить всё равно лучше, чем промолчать.
                  notifyIncoming({
                    chatId: message.chatId,
                    message: message.message,
                    openChatId: openChatId ?? null,
                    chats: chatsRef.current,
                  });
                });
            }
          }
          break;
        }

        case "chat-read-outbox": {
          // Собеседник дочитал до N: у всех наших сообщений до N включительно
          // вторая галочка. Сравнение по идентификатору Telegram, а не по
          // нашему UUID — только он монотонен внутри чата.
          if (activeChatRef.current?.id !== message.chatId) break;
          const last = toBigInt(message.lastReadOutboxMessageId);
          if (last === null) break;
          setMessages((prev) =>
            prev.map((m) => {
              if (m.direction !== "OUTBOUND" || m.read) return m;
              const own = toBigInt(m.telegramMessageId ?? null);
              return own !== null && own <= last ? { ...m, read: true } : m;
            }),
          );
          break;
        }

        case "chat-action": {
          const chat = chatsRef.current.find((c) => c.id === message.chatId);
          const label = chatActionLabel(
            message.action,
            message.senderName,
            chat?.type ?? "private",
          );
          setActivity((prev) => {
            if (!label) {
              if (!(message.chatId in prev)) return prev;
              const rest = { ...prev };
              delete rest[message.chatId];
              return rest;
            }
            return {
              ...prev,
              [message.chatId]: { label, until: Date.now() + CHAT_ACTION_TTL_MS },
            };
          });
          break;
        }

        case "account-state":
          setAccounts((prev) =>
            prev.map((a) =>
              a.id === message.account.id ? message.account : a,
            ),
          );
          break;

        case "revoked":
          // Доступ отобрали прямо сейчас. Молча оставить пользователя на экране
          // чата, который перестал обновляться, — худший вариант: он решит,
          // что система зависла.
          void reloadAccounts();
          // Закрываем открытый чат, только если отозвана именно его комната
          // или комната его аккаунта: отзыв доступа к соседнему аккаунту не
          // повод выбрасывать менеджера из переписки.
          setActiveChat((prev) =>
            prev &&
            (message.room === chatRoom(prev.id) ||
              message.room === accountRoom(prev.account.id))
              ? null
              : prev,
          );
          break;
      }
    });

    client.connect();

    return () => {
      offStatus();
      offMessage();
      client.disconnect();
      realtime.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Данные ─────────────────────────────────────────────────────────────────

  /**
   * Сообщить Telegram, что чат прочитан.
   *
   * ── Что здесь чинится ─────────────────────────────────────────────────
   *
   * Счётчик непрочитанного после просмотра не уменьшался. Интерфейс читал
   * историю, но Telegram о просмотре не знал: это разные операции, и
   * вторую никто не делал. Теперь открытие чата и каждое входящее в открытый
   * чат уходят как «просмотрено». Счётчик в списке обнуляется сразу, не
   * дожидаясь подтверждения: чат уже на экране.
   *
   * Ошибку глотаем осознанно: сбой отметки не должен мешать читать. Telegram
   * пришлёт свой счётчик следующим апдейтом, и расхождение само исправится.
   */
  const markRead = useCallback(
    (chatId: string, telegramMessageIds: string[] = []) => {
      setChats((prev) =>
        prev.map((c) => (c.id === chatId && c.unreadCount > 0 ? { ...c, unreadCount: 0 } : c)),
      );
      void api
        .markRead(chatId, telegramMessageIds)
        .then(() => refreshUnread())
        .catch(() => undefined);
    },
    [api, refreshUnread],
  );

  const reloadAccounts = useCallback(async () => {
    const { items } = await api.listAccounts();
    setAccounts(items);

    // Подписываемся на комнаты доступных аккаунтов. Сервер всё равно проверит
    // права — здесь мы лишь не просим заведомо лишнего.
    for (const account of items)
      realtime.current?.subscribe(accountRoom(account.id));
  }, [api]);

  const inboxParams = useCallback(
    () => ({
      ...(selectedAccount.kind === "account"
        ? { telegramAccountId: selectedAccount.accountId }
        : {}),
      ...(selectedAccount.kind === "team"
        ? { telegramAccountIds: selectedAccount.accountIds }
        : {}),
      ...(unreadOnly ? { unread: true } : {}),
      ...(archivedView ? { archived: true } : {}),
      ...(activeFolderId ? { folderId: activeFolderId } : {}),
      ...(activeTgFolder !== null ? { tgFolderId: activeTgFolder } : {}),
      ...(debouncedSearch.trim() ? { search: debouncedSearch.trim() } : {}),
      limit: 50,
    }),
    [selectedAccount, unreadOnly, debouncedSearch, archivedView, activeFolderId, activeTgFolder],
  );

  useEffect(() => {
    filteredRef.current =
      unreadOnly ||
      archivedView ||
      activeFolderId !== null ||
      activeTgFolder !== null ||
      debouncedSearch.trim() !== "";
  }, [unreadOnly, archivedView, activeFolderId, activeTgFolder, debouncedSearch]);

  /*
   * Папки Telegram грузим только когда выбран один аккаунт: их id (tgId)
   * уникальны в пределах аккаунта, и показывать их для «Все» или команды
   * бессмысленно — папки у аккаунтов разные.
   */
  useEffect(() => {
    if (selectedAccount.kind !== "account") {
      setTgFolders([]);
      setActiveTgFolder(null);
      return;
    }
    const accountId = selectedAccount.accountId;
    let cancelled = false;
    setActiveTgFolder(null);
    api
      .telegramFolders(accountId)
      .then(({ items }) => {
        if (!cancelled) setTgFolders(items);
      })
      .catch(() => {
        if (!cancelled) setTgFolders([]);
      });
    return () => {
      cancelled = true;
    };
  }, [api, selectedAccount]);

  const reloadChats = useCallback(async () => {
    const seq = ++chatsSeq.current;
    setLoadingChats(true);
    setLoadMoreFailed(false);
    try {
      const { items, nextCursor } = await api.inbox(inboxParams());
      if (seq !== chatsSeq.current) return;
      setChats(items);
      setChatsCursor(nextCursor);
    } finally {
      if (seq === chatsSeq.current) setLoadingChats(false);
    }
  }, [api, inboxParams]);

  /*
   * Следующая страница списка.
   *
   * ── Что здесь чинится ─────────────────────────────────────────────────
   *
   * Список брал первые 50 чатов и на этом останавливался: курсор из ответа
   * выбрасывался. У аккаунта с сотнями диалогов всё, что старше полусотни,
   * из интерфейса было недостижимо — и выглядело это как «подтянулись не все
   * чаты», хотя в базе они были. Теперь список докачивается по мере
   * прокрутки.
   */
  const loadMoreChats = useCallback(async () => {
    if (!chatsCursor || loadingMoreChats) return;
    // Страница принадлежит списку, под который её запросили. Если за это
    // время список перечитали под другой фильтр — дописывать её некуда.
    const seq = chatsSeq.current;
    setLoadingMoreChats(true);
    setLoadMoreFailed(false);
    try {
      const { items, nextCursor } = await api.inbox({
        ...inboxParams(),
        cursor: chatsCursor,
      });
      if (seq !== chatsSeq.current) return;
      // Чат, пришедший по WebSocket, пока листали, уже может быть в списке.
      setChats((prev) => {
        const known = new Set(prev.map((c) => c.id));
        return [...prev, ...items.filter((c) => !known.has(c.id))];
      });
      setChatsCursor(nextCursor);
    } catch {
      if (seq === chatsSeq.current) setLoadMoreFailed(true);
    } finally {
      setLoadingMoreChats(false);
    }
  }, [api, inboxParams, chatsCursor, loadingMoreChats]);

  // Стабильная ссылка для списка: новая функция на каждую отрисовку
  // пересоздавала наблюдатель за прокруткой и дёргала подгрузку повторно.
  const onLoadMoreChats = useCallback(() => void loadMoreChats(), [loadMoreChats]);

  const reloadFolders = useCallback(async () => {
    const { items } = await api.listFolders();
    setFolders(items);
  }, [api]);

  useEffect(() => {
    void reloadAccounts();
    void reloadFolders();
  }, [reloadAccounts, reloadFolders]);

  useEffect(() => {
    void reloadChats();
  }, [reloadChats]);

  // ── Выбор чата ─────────────────────────────────────────────────────────────

  /**
   * Загрузить историю чата, не очищая ленту.
   *
   * Применяется только ответ на последний запрос и только если чат всё ещё
   * открыт. Без этой проверки при быстром переходе A → B поздний ответ по A
   * подменял ленту B, а с ней отметку о прочтении и ошибку под шапкой.
   */
  const loadHistory = useCallback(
    async (chat: TelegramChatDto, initial = false) => {
      const seq = ++historySeq.current;
      const current = (): boolean =>
        seq === historySeq.current && activeChatRef.current?.id === chat.id;

      try {
        const { items, nextCursor } = await api.messages(chat.id, { limit: 50 });
        if (!current()) return;
        if (initial) {
          setMessages(items);
          setOlderCursor(items.length > 0 ? nextCursor : null);
        } else {
          // Перечитывание после отправки или правки не должно выбрасывать то,
          // что человек уже подгрузил прокруткой вверх.
          setMessages((prev) => mergeLatest(prev, items));
        }
        setHistoryError(null);
        // Просмотр засчитывается по показанным сообщениям: их номера в
        // Telegram и уходят туда. Наш UUID сервер не примет (400).
        if (chat.unreadCount > 0 || items.length > 0) {
          markRead(
            chat.id,
            items.map(telegramIdOf).filter((id): id is string => id !== null),
          );
        }
      } catch {
        if (!current()) return;
        // Проглатывать эту ошибку нельзя. Пустой список выглядит как «переписки
        // не было», хотя на деле историю не удалось загрузить — например,
        // аккаунт отключён. Разница принципиальная: в первом случае менеджер
        // спокойно пишет первым, во втором его сообщение никуда не уйдёт.
        setHistoryError(
          chat.account.connectionState === "CONNECTED"
            ? "Не удалось загрузить историю сообщений"
            : `Аккаунт ${chat.account.alias} не подключён — история недоступна`,
        );
      }
    },
    [api, markRead, setOlderCursor],
  );

  /**
   * Подгрузить более ранние сообщения — лента дошла до верхнего края.
   *
   * Одна подгрузка за раз: событие прокрутки сыплется десятками, и без
   * замка в TDLib ушла бы пачка одинаковых запросов.
   */
  const loadOlder = useCallback(async () => {
    const chat = activeChatRef.current;
    const cursor = olderCursorRef.current;
    if (!chat || !cursor || loadingOlderRef.current) return;

    loadingOlderRef.current = true;
    setLoadingOlder(true);
    try {
      const { items, nextCursor } = await api.messages(chat.id, { limit: 50, cursor });
      if (activeChatRef.current?.id !== chat.id) return;

      setMessages((prev) => prependOlder(prev, items));
      // Пустая страница или курсор на месте — это начало переписки.
      setOlderCursor(items.length > 0 && nextCursor !== cursor ? nextCursor : null);
    } catch {
      // Курсор оставляем: подгрузка повторится при следующей прокрутке.
    } finally {
      loadingOlderRef.current = false;
      setLoadingOlder(false);
    }
  }, [api, setOlderCursor]);

  /*
   * Перечитать открытый чат после отправки, правки, реакции.
   *
   * Раньше для этого звали openChat: лента очищалась, мигала пустотой и
   * прыгала в самый низ, сбрасывая место чтения. Здесь лента остаётся на
   * экране, пока не придёт новая.
   */
  const reloadMessages = useCallback(() => {
    const chat = activeChatRef.current;
    if (chat) void loadHistory(chat);
  }, [loadHistory]);

  useEffect(() => {
    resyncRef.current = () => {
      void reloadChats();
      reloadMessages();
    };
  }, [reloadChats, reloadMessages]);

  const openChat = useCallback(
    async (chat: TelegramChatDto) => {
      // Ref — сразу, не дожидаясь эффекта: по нему loadHistory решает, чей
      // это ответ, и он должен знать о переходе раньше, чем придёт ответ.
      activeChatRef.current = chat;
      setActiveChat(chat);
      setMessages([]);
      setOlderCursor(null);
      // Цитата принадлежит покинутому чату: в новом её адресат не существует.
      setReplyTo(null);
      // Выделение из прошлого чата к новому не относится.
      setSelectedMessageIds(new Set());
      setHistoryError(null);

      realtime.current?.subscribe(chatRoom(chat.id));
      // Внутренний индикатор присутствия. В Telegram не транслируется (§30 ТЗ).
      realtime.current?.presence(chat.id, "viewing");

      await loadHistory(chat, true);
    },
    [loadHistory, setOlderCursor],
  );

  /**
   * Переход по ссылке на человека или канал внутри Telegram.
   *
   * Аккаунт берём у ОТКРЫТОГО чата, а не выбранный в фильтре: ссылка пришла в
   * конкретной переписке, и открывать её нужно тем же рабочим аккаунтом.
   * Открытие чужим аккаунтом — не мелочь: канал увидит подписку не того
   * пользователя, а у менеджера может не быть прав на этот аккаунт вовсе.
   */
  const openPeer = useCallback(
    async (username: string) => {
      const accountId = activeChatRef.current?.account.id;
      if (!accountId) return;

      try {
        const chat = await api.openTelegramChat({
          telegramAccountId: accountId,
          username,
        });
        // Как и в поиске: у новой переписки нет lastMessageAt, и сама она в
        // inbox не появится — кладём в список руками.
        setChats((prev) => upsertChat(prev, chat));
        await openChat(chat);
      } catch {
        setHistoryError(`Не удалось открыть @${username}`);
      }
    },
    [api, openChat],
  );

  // Уход с чата снимает индикатор: иначе коллеги будут считать, что менеджер
  // всё ещё в диалоге, и не возьмут его в работу.
  // Зависимость — id, а не объект: chat-updated по открытому чату подменяет
  // объект, и очистка отписывала от комнаты чата, который никто не покидал.
  const activeChatId = activeChat?.id ?? null;
  useEffect(() => {
    if (!activeChatId) return;
    return () => {
      realtime.current?.presence(activeChatId, "idle");
      realtime.current?.unsubscribe(chatRoom(activeChatId));
    };
  }, [activeChatId]);

  /*
   * Переход из пуша: чат, отложенный в deepLink, — при появлении раздела и
   * по событию, если раздел уже на экране.
   */
  useEffect(() => {
    const open = () => {
      const id = takePendingChat();
      if (!id) return;
      void api
        .chat(id)
        .then((chat) => {
          setChats((prev) => upsertChat(prev, chat));
          void openChat(chat);
        })
        .catch(() => setHistoryError("Не удалось открыть чат из уведомления"));
    };
    open();
    window.addEventListener(OPEN_CHAT_EVENT, open);
    return () => window.removeEventListener(OPEN_CHAT_EVENT, open);
  }, [api, openChat]);

  /** Закрыть переписку (телефон): назад к списку. */
  const closeChat = useCallback(() => {
    activeChatRef.current = null;
    setMobileDetails(false);
    setActiveChat(null);
    setMessages([]);
    setOlderCursor(null);
    setReplyTo(null);
    setSelectedMessageIds(new Set());
  }, [setOlderCursor]);

  /*
   * Системное «Назад» на телефоне (кнопка Android, свайп на iPhone).
   *
   * Без записи в историю «назад» из открытой переписки закрывало бы всё
   * приложение — установленное PWA не имеет своей адресной строки, и выйти
   * из него на главный экран телефона вместо списка чатов — очень странно.
   * Открытие чата и карточки кладёт по записи; «назад» снимает верхнюю:
   * сперва карточку, потом переписку. Смена чата внутри переписки (ссылка на
   * человека) записи не множит — replaceState.
   */
  const inChat = mobile && activeChatId !== null;
  // В открытой переписке нижняя панель разделов прячется (tailwind.css).
  useEffect(() => {
    document.documentElement.toggleAttribute("data-tw-chat-open", inChat);
    return () => document.documentElement.removeAttribute("data-tw-chat-open");
  }, [inChat]);
  useEffect(() => {
    if (!inChat) return;
    if (window.history.state?.tw === "chat") window.history.replaceState({ tw: "chat" }, "");
    else window.history.pushState({ tw: "chat" }, "");
  }, [inChat, activeChatId]);
  useEffect(() => {
    if (!mobile || !mobileDetails) return;
    window.history.pushState({ tw: "details" }, "");
  }, [mobile, mobileDetails]);
  useEffect(() => {
    if (!mobile) return;
    const onPop = () => {
      const state = window.history.state?.tw;
      if (state === "chat") setMobileDetails(false);
      else closeChat();
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [mobile, closeChat]);

  /** Кнопка «Назад» в шапке переписки — то же, что системная. */
  const goBack = useCallback(() => {
    const state = window.history.state?.tw;
    if (state === "chat" || state === "details") window.history.back();
    else closeChat();
  }, [closeChat]);

  /**
   * Выполнить массовое действие и разобрать ответ.
   *
   * Пропущенные чаты показываются отдельно: сервер мог отказать по части
   * выбранного, если доступ отозвали между показом списка и нажатием кнопки.
   * Молча применить к остальному значило бы соврать о результате.
   */
  const runBulk = useCallback(
    async (input: {
      action: "archive" | "status" | "delete";
      status?: string;
    }) => {
      setBulkBusy(true);
      setHistoryError(null);
      try {
        const result = await api.bulkChats({
          chatIds: [...selectedIds],
          action: input.action,
          ...(input.status ? { status: input.status } : {}),
        });

        if (result.skipped.length > 0) {
          setHistoryError(
            `Готово: ${result.applied}. Пропущено ${result.skipped.length} — нет доступа.`,
          );
        }

        setSelectedIds(new Set());
        setActiveChat(null);
        await reloadChats();
      } catch (error) {
        setHistoryError(
          error instanceof Error
            ? error.message
            : "Не удалось выполнить действие",
        );
      } finally {
        setBulkBusy(false);
      }
    },
    [api, selectedIds, reloadChats],
  );

  const visibleChats = useMemo(() => {
    if (selectedAccount.kind === "account") {
      return chats.filter((c) => c.account.id === selectedAccount.accountId);
    }
    if (selectedAccount.kind === "team") {
      const ids = new Set(selectedAccount.accountIds);
      return chats.filter((c) => ids.has(c.account.id));
    }
    return chats;
  }, [chats, selectedAccount]);

  return (
    <div
      className={cn(
        "grid h-full min-h-0 gap-4 max-[860px]:grid-cols-[minmax(0,1fr)]",
        mobile && "!grid-cols-[minmax(0,1fr)] gap-0",
        /*
         * Четыре сочетания колонок вместо двух.
         *
         * Ширину задаёт сетка, а не сами колонки: спрятать карточку одним
         * `hidden` мало — её 300 пикселей остались бы пустой полосой справа,
         * и переписка не стала бы шире ни на пиксель. Ровно ради ширины её и
         * закрывают.
         */
        sidebarOpen &&
          detailsOpen &&
          "grid-cols-[320px_minmax(0,1fr)_300px] max-[1200px]:grid-cols-[290px_minmax(0,1fr)]",
        sidebarOpen &&
          !detailsOpen &&
          "grid-cols-[320px_minmax(0,1fr)] max-[1200px]:grid-cols-[290px_minmax(0,1fr)]",
        !sidebarOpen &&
          detailsOpen &&
          "grid-cols-[minmax(0,1fr)_300px] max-[1200px]:grid-cols-[minmax(0,1fr)]",
        !sidebarOpen && !detailsOpen && "grid-cols-[minmax(0,1fr)]",
      )}
    >
      <aside
        className={cn(
          "flex min-h-0 flex-col overflow-hidden rounded-[20px] bg-card ring-1 ring-black/[0.05] [box-shadow:var(--panel-shadow)] dark:ring-white/10",
          !mobile && "max-[860px]:hidden",
          !mobile && !sidebarOpen && "hidden",
          mobile && "rounded-none ring-0 [box-shadow:none]",
          mobile && activeChat && "hidden",
        )}
      >
        <AccountSidebar
          accounts={accounts}
          selected={selectedAccount}
          onSelect={setSelectedAccount}
          connectionStatus={wsStatus}
        />

        {/*
          Строка поиска.

          Кнопка «Новый контакт» переехала сюда из отдельного ряда под полем.
          Ряд занимал высоту постоянно ради действия, которым пользуются
          изредка, — а высота на узкой панели и есть главный дефицит: до
          первого чата было четыре служебных ряда.
        */}
        <div className="flex flex-none items-center gap-1.5 border-b border-border p-2">
          <div className="relative flex min-w-0 flex-1 items-center">
            <SearchIcon className="pointer-events-none absolute left-3 size-4 text-muted-foreground" />
            <input
              className="h-9 w-full rounded-full border border-transparent bg-muted pl-9 pr-3 text-sm outline-none transition-colors focus:border-ring focus:bg-background"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Поиск по чатам"
              type="search"
            />
          </div>

          <button
            type="button"
            onClick={() => setTelegramSearchOpen((v) => !v)}
            aria-expanded={telegramSearchOpen}
            aria-label="Найти в Telegram и начать переписку"
            title="Найти в Telegram и начать переписку"
            className={cn(
              // 36 пикселей — не меньше, чем у поля рядом: кнопка размером с
              // значок промахивается мимо пальца и мимо курсора одинаково.
              "grid size-9 flex-none place-items-center rounded-full border transition-colors",
              telegramSearchOpen
                ? "border-brand bg-secondary text-brand"
                : "border-border bg-transparent text-muted-foreground hover:border-brand hover:text-brand",
            )}
          >
            <PlusIcon className="size-4" />
          </button>
        </div>

        {/*
          Поиск по Telegram стоит ПОД полем поиска по нашим чатам и открывается
          отдельно. Порядок не случаен: сперва менеджер ищет там, где переписка
          уже есть, и только не найдя — идёт в Telegram. Обратный порядок гнал
          бы лишние запросы к Telegram на каждый привычный поиск.
        */}
        <TelegramSearch
          open={telegramSearchOpen}
          onOpenChange={setTelegramSearchOpen}
          accounts={accounts}
          selectedAccount={selectedAccount}
          onOpened={(chat) => {
            /*
             * Чат уже заведён на сервере. Кладём его в список сами, не дожидаясь
             * перечитывания: у новой переписки нет ни одного сообщения, а
             * значит нет и lastMessageAt, по которому строится inbox — она бы
             * просто не попала в выборку, и менеджер решил бы, что не сработало.
             */
            setChats((prev) => upsertChat(prev, chat));
            void openChat(chat);
          }}
        />

        {/*
          Папки Telegram выбранного аккаунта — как в самом Telegram.
          Отдельным рядом над нашими фильтрами: это чужая структура (папки
          сотрудника в его телефоне), и мешать её с нашими статусами не стоит.
          Появляется только при выбранном одном аккаунте и только если папки
          у него есть.
        */}
        {selectedAccount.kind === "account" && tgFolders.length > 0 && (
          <div className="flex flex-none items-center gap-1.5 overflow-x-auto border-b border-border/60 bg-card/60 px-3 py-1.5">
            <button
              type="button"
              aria-pressed={activeTgFolder === null}
              onClick={() => setActiveTgFolder(null)}
              className={cn(FILTER_PILL, activeTgFolder === null ? FILTER_PILL_ON : FILTER_PILL_OFF)}
            >
              Все чаты
            </button>
            {tgFolders.map((folder) => (
              <button
                key={folder.tgId}
                type="button"
                aria-pressed={activeTgFolder === folder.tgId}
                onClick={() =>
                  setActiveTgFolder((prev) => (prev === folder.tgId ? null : folder.tgId))
                }
                className={cn(
                  FILTER_PILL,
                  "flex-none whitespace-nowrap",
                  activeTgFolder === folder.tgId ? FILTER_PILL_ON : FILTER_PILL_OFF,
                )}
                title={folder.title}
              >
                {folder.title}
              </button>
            ))}
          </div>
        )}

        <FolderBar
          /*
            Фильтры и папки — один ряд.
            
            Оба отвечают на вопрос «какие чаты показывать», и разделять их на
            два ряда с отдельными рамками означало платить высотой за
            типографскую разницу между ними.
          */
          leading={
            <>
              <button
                type="button"
                aria-pressed={unreadOnly}
                onClick={() => setUnreadOnly((v) => !v)}
                className={cn(
                  FILTER_PILL,
                  unreadOnly ? FILTER_PILL_ON : FILTER_PILL_OFF,
                )}
              >
                Непрочитанные
              </button>

              {/* Архив — отдельный вид, а не ещё один фильтр поверх рабочего
                  списка: смешивать завершённые чаты с активными бессмысленно. */}
              <button
                type="button"
                aria-pressed={archivedView}
                onClick={() => {
                  setArchivedView((v) => !v);
                  // Открытый чат мог только что уехать в архив — в новом
                  // списке его нет, и держать его открытым нельзя.
                  setActiveChat(null);
                }}
                className={cn(
                  FILTER_PILL,
                  archivedView ? FILTER_PILL_ON : FILTER_PILL_OFF,
                )}
              >
                Архив
              </button>
            </>
          }
          folders={folders}
          activeFolderId={activeFolderId}
          onSelect={(folderId) => {
            setActiveFolderId(folderId);
            // Выбор из прошлой папки к новой не относится — иначе массовое
            // действие применилось бы к чатам, которых пользователь уже не видит.
            setSelectedIds(new Set());
          }}
          onChanged={() => void reloadFolders()}
        />

        <ChatList
          chats={visibleChats}
          activity={activity}
          activeChatId={activeChat?.id ?? null}
          loading={loadingChats}
          /* Метка аккаунта нужна только в режиме «Все аккаунты»: в режиме
             одного аккаунта она была бы шумом на каждой строке. */
          showAccountBadge={selectedAccount.kind !== "account"}
          onSelect={(chat) => void openChat(chat)}
          hasMore={chatsCursor !== null}
          loadingMore={loadingMoreChats}
          onLoadMore={onLoadMoreChats}
          loadMoreFailed={loadMoreFailed}
          selectedIds={selectedIds}
          onToggleSelect={(chatId) =>
            setSelectedIds((prev) => {
              const next = new Set(prev);
              if (next.has(chatId)) next.delete(chatId);
              else next.add(chatId);
              return next;
            })
          }
        />

        {selectedIds.size > 0 && (
          <BulkBar
            selectedCount={selectedIds.size}
            totalCount={visibleChats.length}
            busy={bulkBusy}
            canDelete={can("telegram.chat.delete")}
            onSelectAll={() =>
              setSelectedIds(new Set(visibleChats.map((c) => c.id)))
            }
            onClear={() => setSelectedIds(new Set())}
            onArchive={() => void runBulk({ action: "archive" })}
            onStatus={(status) => void runBulk({ action: "status", status })}
            onDelete={() => {
              // Удаление чата необратимо и уносит внутренние заметки вместе с
              // ним — подтверждение обязательно, и оно называет число.
              const ok = window.confirm(
                `Удалить ${selectedIds.size} чатов из системы?\n\n` +
                  "Внутренние заметки, теги и назначения пропадут. В Telegram " +
                  "переписка останется. Действие необратимо.",
              );
              if (ok) void runBulk({ action: "delete" });
            }}
          />
        )}
      </aside>

      <main
        className={cn(
          "flex min-h-0 flex-col overflow-hidden rounded-[20px] bg-[var(--chat-bg)] ring-1 ring-black/[0.05] [box-shadow:var(--panel-shadow)] dark:ring-white/10",
          mobile && "rounded-none ring-0 [box-shadow:none]",
          mobile && !activeChat && "hidden",
        )}
      >
        {activeChat ? (
          <>
            {historyError && (
              <div
                role="alert"
                className="flex flex-none items-center gap-2 border-b border-border bg-[var(--warn)]/12 px-5 py-2.5 text-sm text-[var(--warn)]"
              >
                <AlertIcon className="size-4 shrink-0" />
                {historyError}
              </div>
            )}
            <MessageList
              chat={activeChat}
              {...(mobile ? { onBack: goBack, onOpenDetails: () => setMobileDetails(true) } : {})}
              activity={activity[activeChat.id]?.label ?? null}
              messages={messages}
              hasOlder={olderCursor !== null}
              loadingOlder={loadingOlder}
              onLoadOlder={() => void loadOlder()}
              onReload={reloadMessages}
              selectedIds={selectedMessageIds}
              onOpenPeer={(username) => void openPeer(username)}
              onReply={(message) => {
                // У отправленных через систему id — наш UUID, и ответ на него
                // сервер отвергал. Нужен номер в Telegram.
                const telegramMessageId = telegramIdOf(message);
                if (!telegramMessageId) return;

                setReplyTo({
                  telegramMessageId,
                  preview: messageSummary(message),
                });
              }}
              onToggleSelect={(messageId) =>
                setSelectedMessageIds((prev) => {
                  const next = new Set(prev);
                  if (next.has(messageId)) next.delete(messageId);
                  else next.add(messageId);
                  return next;
                })
              }
            />

            {selectedMessageIds.size > 0 && (
              <ForwardBar
                count={selectedMessageIds.size}
                sourceChat={activeChat}
                chats={chats}
                busy={bulkBusy}
                onCancel={() => setSelectedMessageIds(new Set())}
                onForward={(targetChatId) => {
                  void (async () => {
                    setBulkBusy(true);
                    setHistoryError(null);
                    try {
                      // Выделение держится по id ленты, а пересылке нужны
                      // номера Telegram: UUID сервер передал бы как номер.
                      const telegramIds = messages
                        .filter((m) => selectedMessageIds.has(m.id))
                        .map(telegramIdOf)
                        .filter((id): id is string => id !== null);
                      const result = await api.forwardMessages(
                        activeChat.id,
                        targetChatId,
                        telegramIds,
                      );
                      // Telegram может запретить пересылку из чата-источника,
                      // и часть пачки не пройдёт. Молчать об этом нельзя.
                      if (result.forwarded < result.requested) {
                        setHistoryError(
                          `Переслано ${result.forwarded} из ${result.requested}: ` +
                            "остальные Telegram пересылать не дал.",
                        );
                      }
                      setSelectedMessageIds(new Set());
                    } catch (error) {
                      setHistoryError(
                        error instanceof Error
                          ? error.message
                          : "Не удалось переслать",
                      );
                    } finally {
                      setBulkBusy(false);
                    }
                  })();
                }}
              />
            )}
            <Composer
              chatId={activeChat.id}
              chatType={activeChat.type}
              account={activeChat.account}
              replyTo={replyTo}
              onCancelReply={() => setReplyTo(null)}
              onSent={(sentChatId) => {
                // Отправка могла закончиться, когда открыт уже другой чат:
                // его цитату и ленту она не касается.
                if (activeChatRef.current?.id !== sentChatId) return;
                // Ответ одноразовый: цитата снимается вместе с отправкой,
                // иначе следующее сообщение уйдёт ответом на то же самое.
                setReplyTo(null);
                reloadMessages();
              }}
            />
          </>
        ) : (
          <div className="grid h-full place-items-center px-6">
            <div className="flex max-w-xs flex-col items-center gap-2 text-center">
              <EmptyChatArt className="size-32" />
              <span className="rounded-full bg-[var(--date-pill-bg)] px-4 py-2 text-sm text-[var(--date-pill-text)] backdrop-blur">
                {sidebarOpen
                  ? "Выберите чат слева"
                  : "Откройте список чатов в шапке"}
              </span>
            </div>
          </div>
        )}
      </main>

      <aside
        className={cn(
          "min-h-0 overflow-y-auto rounded-[20px] bg-card ring-1 ring-black/[0.05] [box-shadow:var(--panel-shadow)] dark:ring-white/10",
          !mobile && "max-[1200px]:hidden",
          !mobile && !detailsOpen && "hidden",
          // Телефон: карточка — поверх всего экрана, со своей кнопкой «Назад».
          mobile && (mobileDetails && activeChat ? "fixed inset-0 z-50 rounded-none ring-0" : "hidden"),
        )}
        style={mobile ? { paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" } : undefined}
      >
        {mobile && activeChat && (
          <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-border bg-card/95 px-2 py-2 backdrop-blur-xl">
            <button
              type="button"
              onClick={goBack}
              className="inline-flex h-9 items-center gap-1 rounded-full border-0 bg-transparent px-2.5 text-[15px] font-medium text-brand"
            >
              <BackIcon /> Назад
            </button>
            <span className="truncate text-[15px] font-semibold text-heading">Карточка чата</span>
          </div>
        )}
        {/*
          Пустая колонка в 300 пикселей читается как незагрузившийся блок, а не
          как «здесь пока нечего показывать». Заглушка называет, что тут
          появится, — иначе половина экрана выглядит сломанной.
        */}
        {!activeChat && (
          <div className="flex h-full flex-col items-center justify-center gap-2 px-8 text-center">
            <EmptyCardArt className="size-28" />
            <p className="text-sm font-medium text-heading">Карточка чата</p>
            <p className="text-xs leading-relaxed text-muted-foreground">
              Статус, ответственный, папки и внутренние заметки появятся здесь,
              когда вы откроете переписку.
            </p>
          </div>
        )}

        {activeChat && (
          <ChatDetails
            chat={activeChat}
            folders={folders}
            onUpdated={(updated) => {
              // Ответ мог прийти, когда открыт уже другой чат. Подставить его
              // целиком значило бы показать шапку и композер чата A над
              // лентой чата B — и ответ ушёл бы не тому клиенту.
              setActiveChat((prev) => (prev?.id === updated.id ? updated : prev));
              setChats((prev) => replaceChat(prev, updated));
              // Смена статуса могла увести чат из текущего вида — список
              // нужно перечитать, иначе строка останется висеть.
              void reloadChats();
            }}
            onOpenMember={(telegramUserId) => {
              // Открыть личный чат с участником от лица аккаунта этого чата.
              const accountId = activeChatRef.current?.account.id;
              if (!accountId) return;
              void api
                .openTelegramChat({ telegramAccountId: accountId, telegramUserId })
                .then((chat) => {
                  setChats((prev) => upsertChat(prev, chat));
                  void openChat(chat);
                })
                .catch(() => setHistoryError("Не удалось открыть переписку с участником"));
            }}
          />
        )}
      </aside>
    </div>
  );
}

function upsertChat(
  list: TelegramChatDto[],
  chat: TelegramChatDto,
): TelegramChatDto[] {
  const next = list.some((c) => c.id === chat.id)
    ? list.map((c) => (c.id === chat.id ? chat : c))
    : [chat, ...list];

  // Порядок общего inbox — закреплённые в Telegram сверху, дальше по
  // времени последнего сообщения (§5 ТЗ). Тот же порядок, что у сервера.
  return next.sort(
    (a, b) =>
      Number(b.isPinned ?? false) - Number(a.isPinned ?? false) ||
      Date.parse(b.lastMessageAt ?? "0") - Date.parse(a.lastMessageAt ?? "0"),
  );
}

/** Обновить строку, если чат уже в списке; новых не добавлять. */
function replaceChat(
  list: TelegramChatDto[],
  chat: TelegramChatDto,
): TelegramChatDto[] {
  return list.some((c) => c.id === chat.id) ? upsertChat(list, chat) : list;
}

/** Общая форма переключателя фильтра: различаются только состояния. */
const FILTER_PILL =
  "rounded-full px-2.5 py-1 text-[12px] font-medium transition-colors active:scale-[0.97]";

const FILTER_PILL_ON = "border-transparent bg-primary text-primary-foreground";

const FILTER_PILL_OFF =
  "border border-border bg-card text-muted-foreground hover:border-brand hover:text-brand";

/** BigInt из строки идентификатора; null, если строка не число. */
function toBigInt(value: string | null): bigint | null {
  if (!value) return null;
  try {
    return BigInt(value);
  } catch {
    return null;
  }
}

/** Стрелка «Назад» для телефонных шапок. */
function BackIcon() {
  return (
    <svg viewBox="0 0 24 24" className="size-5" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M15 18l-6-6 6-6" />
    </svg>
  );
}
